// @TODO The Swipable will be covered as part of the https://autovia.atlassian.net/browse/AVM-1076

/**
 * @render react
 * @name Gallery
*/

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Swipeable } from 'react-swipeable';

import { withPolaris } from 'Components/protons/Polaris';

import { Button } from 'Components/atoms/Button';
import { Image } from 'Components/atoms/Image';
import { Link } from 'Components/molecules/Link';
import { Video } from 'Components/molecules/Video';
import { Slideshow } from 'Components/organisms/Slideshow';

import './_style.scss';

/**
 * Gallery component
 *
 * @param {string} cover - The initial cover image for the gallery. Upon clicking the cover, the gallery will open
 * @param {boolean} mediaLazyLoad - Defines whether the media need to be lazyloaded or not
 * @param {number} index - The image index we want to show by default when opening the gallery
 * @param {string} mode - The mode we want. Can either be open,close, fullscreen, swipe, list
 * @param {string} url - URL for the linkout mode
 * @param {array} media - Array of media with objects with the thumb and large keys
 * @param {string} viewAllUrl - URL for for View All link
 * @param {string} viewAllLabel - Label for View All link
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {string} badge - object containing src (string) to the icon background image and alt text (string)
 */
const Gallery = ({
  cover,
  mediaLazyLoad,
  index: initialIndex,
  mode: initialMode,
  url,
  viewAllUrl,
  viewAllLabel,
  media,
  totalMedia: propTotalMedia,
  displayArrows,
  loadStyles,
  extraClassNames,
  context,
  badge,
}) => {
  // Compute clamped numeric index
  const numericIndex = typeof initialIndex === 'number' ? initialIndex : 0;
  const clampedIndex = Math.max(0, Math.min(numericIndex, media.length - 1));

  // Set the initial state
  const [currentIndex, setCurrentIndex] = useState(clampedIndex);
  const currentMode = initialMode ?? 'swipe';
  const currentTotalMedia = propTotalMedia ?? media.length;

  /**
   * Returns the correct Component depending on the media provided
   * @param {object} contentMedia
   * @param index
   */
  const getContent = useCallback((contentMedia, contentIndex) => {
    const { cssPrefix } = context;
    let lazyLoadEnabled = mediaLazyLoad;
    let isLCPCandidate = contentMedia.isLCPCandidate || false;

    if (!contentMedia) {
      return false;
    }

    if (contentMedia.__typename === 'Video') {
      if (contentMedia.url || contentMedia.src) {
        return (
          <>
            <Video
              url={contentMedia.url || contentMedia.src}
              playing={false}
            />
            {contentMedia.caption && (
              <div className={`${cssPrefix}__gallery--caption`}>{contentMedia.caption}</div>
            )}
          </>
        );
      }
      // details about media data type video is yet to be defined
      // @TODO temp
      return (
        <div data-type={contentMedia.__typename} />
      );
    }

    if (contentMedia.__typename === 'LinkedMedia') {
      return null;
    }

    if (contentMedia.__typename === 'Advertisement') {
      return contentMedia.component;
    }

    // Disabling lazyLoad and set isLCPCandidate in case the Gallery mode is list
    // And the item is the first of the list
    if (currentMode === 'list' && contentIndex === 0) {
      lazyLoadEnabled = false;
      isLCPCandidate = true;
    }

    return (
      <Image
        src={contentMedia.large}
        alt={contentMedia.alt}
        srcSet={{ small: contentMedia.large, medium: contentMedia.large, large: contentMedia.large }}
        width={contentMedia.width || '100%'}
        height={contentMedia.height || 'auto'}
        extraClassNames={{ '-pointer-events-none': true }}
        caption={contentMedia.caption}
        credits={contentMedia.credits}
        displayImageMetadata
        size="content-image-full"
        lazyLoad={lazyLoadEnabled}
        isLCPCandidate={isLCPCandidate}
      />
    );
  }, [mediaLazyLoad, context, currentMode]);

  /**
   * Shows the previous image in the gallery
   */
  const showPreviousImage = useCallback(() => {
    setCurrentIndex(prevIndex => (prevIndex - 1 + currentTotalMedia) % currentTotalMedia);
  }, [currentTotalMedia]);

  /**
   * Shows the next image in the gallery
   */
  const showNextImage = useCallback(() => {
    setCurrentIndex(prevIndex => (prevIndex + 1) % currentTotalMedia);
  }, [currentTotalMedia]);

  if (media.length === 0) return null;

  const { cssPrefix } = context;

  const pushTracking = () => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'linkout_gallery.button_click',
      });
    }
  };

  return (
    <div
      className={classNames({
        [`${cssPrefix}__gallery`]: loadStyles,
        [`-${currentMode}`]: true,
        '-has-badge': badge && badge.src,
        ...extraClassNames,
      })}
    >
      { badge && badge.src !== '' && (
        <Image
          {...badge}
          extraClassNames={{ [`${cssPrefix}__gallery--badge`]: true }}
        />
      )}

      { currentMode === 'slideshow' && (
        <Slideshow media={media} />
      )}

      { currentMode === 'linkout' && (
        <div className={`${cssPrefix}__gallery--cover`}>
          {cover && cover.src && (
            <Link
              href={url}
              onClick={pushTracking}
            >
              <Image
                size="content-image-full"
                {...cover}
                lazyLoad={mediaLazyLoad}
              />
              <span className={`${cssPrefix}__gallery--slide-info`}>
                {currentTotalMedia}
              </span>
            </Link>
          )}
        </div>
      )}

      { currentMode === 'list' && (
        <ul className={`${cssPrefix}__gallery--list`}>
          {media.map((content, i) => (
            <li
              key={i}
              className={classNames({
                '-linked-media': content.__typename === 'LinkedMedia',
                '-video': content.__typename === 'Video',
                '-image': content.__typename === 'Image',
                '-ad': content.__typename === 'Advertisement',
              })}
            >
              { getContent(content, i) }
            </li>
          ))}
        </ul>
      )}

      {
        currentMode === 'swipe'
        && (
          <div className={`${cssPrefix}__gallery--cover`}>
            <div className={`${cssPrefix}__gallery--main`}>
              <Swipeable
                onSwipedLeft={showNextImage}
                onSwipedRight={showPreviousImage}
                trackMouse
              >
                { getContent(media[currentIndex]) }
              </Swipeable>

              {displayArrows && (
                <div className={`${cssPrefix}__gallery--navigation`}>
                  <Button label="Previous" onClick={showPreviousImage} extraClassNames={{ [`${cssPrefix}__gallery--previous`]: true }} />
                  <Button label="Next" onClick={showNextImage} extraClassNames={{ [`${cssPrefix}__gallery--next`]: true }} />
                </div>
              )}
            </div>

            <span className={`${cssPrefix}__gallery--slide-info`}>
              {`${currentIndex + 1}/${currentTotalMedia}`}
              {viewAllUrl && <Link href={viewAllUrl}>{viewAllLabel}</Link>}
            </span>
          </div>
        )
      }
    </div>
  );
};

/**
 * Valid props
 */
Gallery.propTypes = {
  cover: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    size: PropTypes.string,
  }),
  mediaLazyLoad: PropTypes.bool,
  index: PropTypes.number,
  mode: PropTypes.oneOf(['open', 'close', 'fullscreen', 'list', 'swipe', 'linkout']),
  url: PropTypes.string,
  viewAllUrl: PropTypes.string,
  viewAllLabel: PropTypes.string,
  media: PropTypes.arrayOf(PropTypes.shape({
    thumb: PropTypes.string,
    large: PropTypes.string,
    video: PropTypes.string,
    caption: PropTypes.string,
    credits: PropTypes.string,
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
  totalMedia: PropTypes.number,
  displayArrows: PropTypes.bool,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
  badge: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

/**
 * Default props
 */
Gallery.defaultProps = {
  cover: null,
  mediaLazyLoad: true,
  index: 0,
  mode: 'swipe',
  url: '',
  viewAllUrl: '',
  viewAllLabel: 'View All',
  media: [],
  totalMedia: null,
  displayArrows: true,
  loadStyles: true,
  extraClassNames: {},
  context: {},
  badge: {
    src: '',
    alt: '',
    height: 0,
    width: 0,
  },
};

export default withPolaris(Gallery);
